/* eslint-disable react-refresh/only-export-components */
/**
 * SettingsContext — 生成設定 (属性・モデル・モード・キャスティング・動画設定など)。
 *
 * localStorage 永続化を伴う「次に生成するときの設定」をまとめて持つ。
 * ScenarioAttributesProvider の内側に置く (visualStyleIsStylized の導出に
 * 属性ツリー由来の stylized マップが必要なため)。
 */
import { splitShootingMethod } from './shootingMethod';
import { normalizeTargetKey } from './targetAudience';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { useScenarioAttributesStateSafe, useVisualStyleStylizedMap } from './scenarioAttributesContext';
import {
  MOTION_GRAPHICS_WHOLE, effectiveMotionGraphics, migrateLegacyMotionVisualStyle, normalizeMotionGraphics,
} from './motionGraphics';
import {
  STORAGE_KEY_ASPECT_RATIO,
  STORAGE_KEY_EXCLUDE_TEXT,
  STORAGE_KEY_INCLUDE_LOGO,
  STORAGE_KEY_INCLUDE_PRODUCT_INFO,
  STORAGE_KEY_INCLUDE_BRAND_INFO,
  STORAGE_KEY_GENERATION_MODEL,
  STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM,
  STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU,
  STORAGE_KEY_MEDIA_PLATFORM,
  STORAGE_KEY_VIDEO_GENERATION_MODE,
  STORAGE_KEY_VISUAL_STYLE,
  STORAGE_KEY_SHOOTING_METHOD,
  STORAGE_KEY_DIALECT,
  STORAGE_KEY_MOTION_GRAPHICS,
  STORAGE_KEY_TARGET_GENDER,
  STORAGE_KEY_TARGET_AGE,
  getInitialSettings,
  loadStringMap,
  saveStringMap,
} from './settingsStorage';
import { STORAGE_KEY_VIDEO_SETTINGS, loadVideoSettingsFromStorage } from './types';
import { resolveVisualStyleStylized } from './visualStyle';
import type {
  AttributeTreeNode,
  CastingActor,
  CastingSelectionState,
  GenerationSettings,
  ProductionMode,
  ScenarioInputTab,
  SeedancePresetActorItem,
  SelectedCasting,
  SelectedSeedancePresetActor,
  VideoGenerationMode,
  VideoSettings,
} from './types';

export type SettingsContextValue = {
  settings: GenerationSettings;
  setSettings: (next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => void;
  /** 生成履歴からの復元用。setSettings と異なり attributes を「丸ごと置換」する（古いキーを残さない）。
   * 他のフィールドは部分指定でそのまま上書き。 */
  restoreSettings: (next: Partial<GenerationSettings> & { attributes: Record<string, unknown> }) => void;
  /** 一時的な設定適用。ephemeralKeys に挙げたキーの変更は localStorage 永続化を
   * 1 回だけスキップする（恒久設定を汚さない）。「AIに修正依頼」で既定値を流し込む際に
   * モデル選好・最終プラットフォーム等を上書きしないために使う。 */
  applyTransientSettings: (partial: Partial<GenerationSettings>, ephemeralKeys: string[]) => void;
  setAttribute: (key: string, value: unknown) => void;
  attributeTree: AttributeTreeNode[];
  setAttributeTree: (nodes: AttributeTreeNode[]) => void;
  maxReferenceCount: number;
  setMaxReferenceCount: (count: number) => void;
  /** video-to-video の参照動画の最大本数 (DB metadata.max_reference_videos 由来、0=非対応)。
   *  動画素材を参考画像モードで選択可能にするかの判定にも使う。component 層から設定する。 */
  maxReferenceVideos: number;
  setMaxReferenceVideos: (count: number) => void;
  /** 参照音声の最大クリップ数 (DB metadata.max_reference_audios 由来、0=非対応)。
   *  音声素材を選択可能にするかの判定にも使う (Seedance 2.0系/H3=3, 2.5=10)。 */
  maxReferenceAudios: number;
  setMaxReferenceAudios: (count: number) => void;
  castingPosition: number;
  setCastingPosition: (position: number) => void;
  pendingPrompt: string | null;
  pendingMode: 'prompt' | 'video' | null;
  setPendingEdit: (prompt: string) => void;
  setPendingVideoGeneration: (prompt?: string) => void;
  clearPendingEdit: () => void;
  /** テンプレートモード (ポスター等) の参照 URL。生成開始時にフルページキャプチャを
   *  個人フォルダへ素材登録し、参照画像として使う。localStorage には永続化しない。 */
  templateReferenceUrl: string;
  setTemplateReferenceUrl: (url: string) => void;
  productionMode: ProductionMode;
  setProductionMode: (mode: ProductionMode) => void;
  videoGenerationMode: VideoGenerationMode;
  setVideoGenerationMode: (mode: VideoGenerationMode) => void;
  setVideoGenerationModeTransient: (mode: VideoGenerationMode) => void;
  restoreVideoGenerationMode: () => void;
  promptVideoGenerationMode: VideoGenerationMode;
  setPromptVideoGenerationMode: (mode: VideoGenerationMode) => void;
  scenarioInputTab: ScenarioInputTab;
  setScenarioInputTab: (tab: ScenarioInputTab) => void;
  selectedCasting: SelectedCasting;
  setSelectedCasting: (casting: SelectedCasting) => void;
  // 複数AI俳優対応 (Phase 2): selectedCasting=登場人物A(主) に加え、追加俳優(B,C…)。
  // フルキャスト = [selectedCasting, ...additionalCastings].filter(Boolean)。
  // 既存の単数 selectedCasting 消費点はそのまま動く加法的設計。
  additionalCastings: CastingActor[];
  setAdditionalCastings: Dispatch<SetStateAction<CastingActor[]>>;
  // キャスト編集状態 (docs/features/character-casting-consistency-plan.md 修正2-a)。
  // シナリオ切替時の DB (casting_model_ids) からの復元は 'inherited' として行い、
  // ユーザーがキャスティングを編集したら 'explicit' へ遷移する。'explicit' の間は
  // DB 再取得によるキャスト上書きを行わない (useCastingHydration が参照する)。
  castingSelectionState: CastingSelectionState;
  setCastingSelectionState: (state: CastingSelectionState) => void;
  /** ユーザーがキャスティングを編集する操作 (俳優選択/追加/解除・Seedanceプリセット選択・
   *  ?model= ディープリンクによる事前選択) から呼ぶ。呼ぶと 'explicit' へ遷移する。 */
  markCastingExplicit: () => void;
  // ?model= ディープリンク由来のキャストの適用対象シナリオ pending 状態
  // (docs/features/character-casting-consistency-plan.md 修正2-a「修正1との相互作用」精緻化・
  // 2026-08-20 実装レビュー P1×2)。boolean の一回限りフラグ (旧 hasPendingDeepLinkCast) では
  // 「どのシナリオに適用する pending か」を表現できず、シナリオ表示中 (hydration 済み) に
  // ?model= が処理されると消費されないまま残り、次のシナリオ切替で持ち越されるバグが
  // あったため、適用対象シナリオID (または PENDING_NEXT_SCENARIO センチネル) で管理する。
  /** null=pending なし。PENDING_NEXT_SCENARIO (scenario/utils.ts)=次に開く任意のシナリオへ
   *  一度だけ適用。具体的なシナリオID=そのシナリオへの適用待ち。 */
  pendingDeepLinkCastScenarioId: string | null;
  /** ?model= ディープリンクの effect が処理直後に呼ぶ
   *  (resolveDeepLinkCastPendingTarget の結果をセットする)。 */
  setPendingDeepLinkCastScenarioId: (scenarioId: string | null) => void;
  /** useCastingHydration が preserve/hydrate を確定した際に呼び、pending を消費 (クリア) する。 */
  consumePendingDeepLinkCast: () => void;
  selectedSeedancePresetActor: SelectedSeedancePresetActor;
  setSelectedSeedancePresetActor: (actor: SelectedSeedancePresetActor) => void;
  // 複数AI俳優対応 (Seedance): selectedSeedancePresetActor=登場人物A(主) に加え、追加 preset(B,C…)。
  additionalSeedancePresetActors: SeedancePresetActorItem[];
  setAdditionalSeedancePresetActors: Dispatch<SetStateAction<SeedancePresetActorItem[]>>;
  videoSettings: VideoSettings;
  setVideoSettings: Dispatch<SetStateAction<VideoSettings>>;
  // ⚠️ 廃止済み機能: 「人物の顔を描かない」モード (黒いサングラス方式)。
  // 「絵コンテをイラストで描く」に一本化され UI トグルは非表示化済み
  // (FirstFrameSettingsSection.tsx)。旧シナリオ metadata / localStorage に
  // true が残っている経路の互換のため state と同期ロジックのみ残置。
  avoidFaces: boolean;
  setAvoidFaces: Dispatch<SetStateAction<boolean>>;
  // 「絵コンテをイラストで描く」モード: ストーリーボード(絵コンテ)を手書き風イラストで
  // 生成し、完成動画は実写にする。avoidFaces (黒いサングラス・廃止済み) とは排他。
  // 参照画像に実在の顔写真を出さないための現行の推奨手段。localStorage 永続化。
  illustrationStoryboard: boolean;
  setIllustrationStoryboard: Dispatch<SetStateAction<boolean>>;
  // シナリオ全体の撮影方法。空文字は指定なし（プロンプト追記なし）。
  shootingMethod: string;
  setShootingMethod: Dispatch<SetStateAction<string>>;
  // シナリオ全体の方言 (属性ツリー dialect の option key)。空文字は指定なし（プロンプト追記なし）。
  dialect: string;
  setDialect: Dispatch<SetStateAction<string>>;
  // シナリオ全体の「モーショングラフィックス」3択 ('' = 指定なし / 'scenes' / 'whole'・計画 md §10.14)。
  // ツリーに無い値は送信時・判定時に指定なし扱い (effectiveMotionGraphics)。localStorage 永続化。
  motionGraphics: string;
  setMotionGraphics: Dispatch<SetStateAction<string>>;
  // シナリオ全体のターゲット性別・年代 (属性ツリー target_gender / target_age の option key)。空文字は指定なし。localStorage 永続化。
  targetGender: string;
  setTargetGender: Dispatch<SetStateAction<string>>;
  targetAge: string;
  setTargetAge: Dispatch<SetStateAction<string>>;
  // 映像スタイル: ビルトイン key または DB 属性ツリー由来 key (OEM 独自含む) の文字列。
  // フレーム/絵コンテ/動画の各プロンプトに反映する。stylized 系選択時は AI俳優/Seedance
  // プリセット/avoid_faces/イラスト絵コンテを無効化する。
  visualStyle: string;
  setVisualStyle: Dispatch<SetStateAction<string>>;
  /** 選択中スタイルが stylized 系か。visualStyle と属性ツリーの visual_family から導出。
   *  「モーショングラフィックス」が全編 (whole) のときも true (コードで描く映像なので実写の人間俳優を弾く・§10.14)。 */
  visualStyleIsStylized: boolean;
  // ストーリーボードの添付オプション (StoryboardCandidates と useVideoHandlers から
  // 参照されるため Generation state に持つ)。localStorage 永続化。
  //   includeStoryboardLogo: ロゴ画像をストーリーボード生成 + 動画生成の両方に添付
  //   includeStoryboardProductImage: 商品メイン画像を動画生成のみ添付
  includeStoryboardLogo: boolean;
  setIncludeStoryboardLogo: Dispatch<SetStateAction<boolean>>;
  includeStoryboardProductImage: boolean;
  setIncludeStoryboardProductImage: Dispatch<SetStateAction<boolean>>;
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function GenerationSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettingsState] = useState<GenerationSettings>(getInitialSettings);
  const [attributeTree, setAttributeTreeState] = useState<AttributeTreeNode[]>([]);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [pendingMode, setPendingMode] = useState<'prompt' | 'video' | null>(null);
  const [templateReferenceUrl, setTemplateReferenceUrl] = useState('');
  const [productionMode, setProductionMode] = useState<ProductionMode>('scenario');
  const [videoGenerationMode, setVideoGenerationModeState] = useState<VideoGenerationMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_VIDEO_GENERATION_MODE);
    if (saved === 'reference' || saved === 'frame') return saved;
    return 'storyboard';
  });
  const setVideoGenerationMode = useCallback((mode: VideoGenerationMode) => {
    setVideoGenerationModeState(mode);
    if (mode) {
      localStorage.setItem(STORAGE_KEY_VIDEO_GENERATION_MODE, mode);
    } else {
      localStorage.removeItem(STORAGE_KEY_VIDEO_GENERATION_MODE);
    }
  }, []);
  // 永続化しない一時セッター。シナリオ閲覧中 (work フェーズ) の表示モード同期で使う。
  // 「次に生成するときのデフォルトモード」(= localStorage) を、たまたま開いたシナリオの
  // frame_mode で上書きしてしまわないため、表示同期は localStorage に書かない。
  const setVideoGenerationModeTransient = useCallback((mode: VideoGenerationMode) => {
    setVideoGenerationModeState(mode);
  }, []);
  // localStorage に保存済みの「ユーザーが明示選択したモード」を state に復元する。
  // 新規シナリオ生成 (setup フェーズ) に戻ったとき、閲覧で一時的に変わった表示モードを
  // 明示選択へ戻すために使う。
  const restoreVideoGenerationMode = useCallback(() => {
    const saved = localStorage.getItem(STORAGE_KEY_VIDEO_GENERATION_MODE);
    setVideoGenerationModeState(saved === 'reference' || saved === 'frame' ? saved : 'storyboard');
  }, []);
  const [promptVideoGenerationMode, setPromptVideoGenerationModeState] = useState<VideoGenerationMode>(() => {
    const saved = localStorage.getItem('video_generation_mode_prompt');
    return saved === 'frame' ? 'frame' : 'reference';
  });
  const setPromptVideoGenerationMode = useCallback((mode: VideoGenerationMode) => {
    setPromptVideoGenerationModeState(mode);
    if (mode) {
      localStorage.setItem('video_generation_mode_prompt', mode);
    }
  }, []);
  const [maxReferenceCount, setMaxReferenceCount] = useState(3);
  const [maxReferenceVideos, setMaxReferenceVideos] = useState(0);
  const [maxReferenceAudios, setMaxReferenceAudios] = useState(0);
  const [castingPosition, setCastingPosition] = useState(0);
  const [scenarioInputTab, setScenarioInputTab] = useState<ScenarioInputTab>('product');
  const [selectedCasting, setSelectedCasting] = useState<SelectedCasting>(null);
  // 複数AI俳優対応 (Phase 2): 追加俳優 (登場人物B,C…)。主俳優 selectedCasting と合わせてフルキャスト。
  const [additionalCastings, setAdditionalCastings] = useState<CastingActor[]>([]);
  // キャスト編集状態 (修正2-a)。既定は 'inherited' (未編集)。
  const [castingSelectionState, setCastingSelectionState] = useState<CastingSelectionState>('inherited');
  const markCastingExplicit = useCallback(() => setCastingSelectionState('explicit'), []);
  // ?model= ディープリンク由来キャストの適用対象シナリオ pending 状態
  // (修正2-a「修正1との相互作用」精緻化)。
  const [pendingDeepLinkCastScenarioId, setPendingDeepLinkCastScenarioId] = useState<string | null>(null);
  const consumePendingDeepLinkCast = useCallback(() => setPendingDeepLinkCastScenarioId(null), []);
  // Seedance preset 関連 state。動画モデル切替時のクリアは consumer 側 (ScenarioGenerator) で実施。
  const [selectedSeedancePresetActorState, setSelectedSeedancePresetActorState] =
    useState<SelectedSeedancePresetActor>(null);
  const setSelectedSeedancePresetActor = useCallback((actor: SelectedSeedancePresetActor) => {
    setSelectedSeedancePresetActorState(actor);
  }, []);
  // 複数AI俳優対応 (Seedance): 追加 preset (登場人物B,C…)。主 + 追加でフルキャスト。
  const [additionalSeedancePresetActors, setAdditionalSeedancePresetActors] =
    useState<SeedancePresetActorItem[]>([]);
  const [videoSettings, setVideoSettings] = useState<VideoSettings>(loadVideoSettingsFromStorage);

  // 「人物の顔を描かない」モード (廃止済み・互換残置): localStorage 永続化
  const [avoidFaces, setAvoidFaces] = useState<boolean>(() => {
    try {
      return localStorage.getItem('avoidFaces') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('avoidFaces', String(avoidFaces));
    } catch {
      // 書き込み失敗は無視
    }
  }, [avoidFaces]);

  // 「絵コンテをイラストで描く」モード: localStorage 永続化。avoidFaces とは排他なので
  // トグル UI 側で一方を ON にしたら他方を OFF にする。
  const [illustrationStoryboard, setIllustrationStoryboard] = useState<boolean>(() => {
    try {
      // 既定 ON: 明示的に 'false' が保存されているときのみ OFF。
      return localStorage.getItem('illustrationStoryboard') !== 'false';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('illustrationStoryboard', String(illustrationStoryboard));
    } catch {
      // 書き込み失敗は無視
    }
  }, [illustrationStoryboard]);

  // 映像スタイル: 'photorealistic'(既定) / 'anime' / 'anime_3d'。localStorage 永続化。
  // シナリオ作成時に generation_metadata.visual_style として保存し、ロード時に復元する。
  // raw key を保持する (OEM 独自 key を潰さない)。空/未設定のみ photorealistic に倒す。
  const [visualStyle, setVisualStyle] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_VISUAL_STYLE);
      return splitShootingMethod({ visual_style: saved }).visualStyle;
    } catch {
      return 'photorealistic';
    }
  });
  const [shootingMethod, setShootingMethod] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SHOOTING_METHOD);
      return splitShootingMethod({
        visual_style: localStorage.getItem(STORAGE_KEY_VISUAL_STYLE),
        ...(saved !== null ? { shooting_method: saved } : {}),
      }).shootingMethod;
    } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_SHOOTING_METHOD, shootingMethod); } catch { /* 保存不可 */ }
  }, [shootingMethod]);
  const [dialect, setDialect] = useState<string>(() => {
    try { return (localStorage.getItem(STORAGE_KEY_DIALECT) ?? '').trim(); } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_DIALECT, dialect); } catch { /* 保存不可 */ }
  }, [dialect]);
  const [motionGraphics, setMotionGraphics] = useState<string>(() => {
    try { return normalizeMotionGraphics(localStorage.getItem(STORAGE_KEY_MOTION_GRAPHICS)); } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_MOTION_GRAPHICS, motionGraphics); } catch { /* 保存不可 */ }
  }, [motionGraphics]);
  const [targetGender, setTargetGender] = useState<string>(() => {
    try { return normalizeTargetKey(localStorage.getItem(STORAGE_KEY_TARGET_GENDER)); } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_TARGET_GENDER, targetGender); } catch { /* 保存不可 */ }
  }, [targetGender]);
  const [targetAge, setTargetAge] = useState<string>(() => {
    try { return normalizeTargetKey(localStorage.getItem(STORAGE_KEY_TARGET_AGE)); } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_TARGET_AGE, targetAge); } catch { /* 保存不可 */ }
  }, [targetAge]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VISUAL_STYLE, visualStyle);
    } catch {
      // 書き込み失敗は無視
    }
  }, [visualStyle]);

  // stylized 判定は DB 属性ツリー由来 (visual_family) のマップと選択中 key から導出する。
  // マップは ScenarioAttributesProvider が供給する (ほぼ不変)。
  const visualStyleStylizedMap = useVisualStyleStylizedMap();
  const motionGraphicsOptions = useScenarioAttributesStateSafe()?.motionGraphicsOptions;
  const isMotionGraphicsWhole = effectiveMotionGraphics(motionGraphics, motionGraphicsOptions ?? []) === MOTION_GRAPHICS_WHOLE;
  const visualStyleIsStylized = useMemo(
    () => isMotionGraphicsWhole || resolveVisualStyleStylized(visualStyle, visualStyleStylizedMap),
    [isMotionGraphicsWhole, visualStyle, visualStyleStylizedMap],
  );
  // 移行後 (ツリーに「モーショングラフィックス」field がある) に旧映像スタイル motion_graphics が残っていたら
  // (localStorage・保存済みシナリオの設定復元)、新項目 whole + 映像スタイル既定へ読み替える (§10.14 互換)
  useEffect(() => {
    const migrated = migrateLegacyMotionVisualStyle(visualStyle, motionGraphicsOptions ?? []);
    if (!migrated) return;
    setVisualStyle(migrated.visualStyle);
    setMotionGraphics(migrated.motionGraphics);
  }, [visualStyle, motionGraphicsOptions]);

  // ストーリーボード添付オプション: localStorage 永続化
  // ロゴは既定 ON / 商品メイン画像は既定 OFF。
  const [includeStoryboardLogo, setIncludeStoryboardLogo] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('storyboard:include_logo');
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch { /* noop */ }
    return true;
  });
  useEffect(() => {
    try { localStorage.setItem('storyboard:include_logo', String(includeStoryboardLogo)); } catch { /* noop */ }
  }, [includeStoryboardLogo]);

  const [includeStoryboardProductImage, setIncludeStoryboardProductImage] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('storyboard:include_product_image');
      if (raw === 'true') return true;
      if (raw === 'false') return false;
    } catch { /* noop */ }
    return false;
  });
  useEffect(() => {
    try { localStorage.setItem('storyboard:include_product_image', String(includeStoryboardProductImage)); } catch { /* noop */ }
  }, [includeStoryboardProductImage]);

  // videoSettings を localStorage に同期
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_VIDEO_SETTINGS, JSON.stringify(videoSettings));
    } catch {
      // 書き込み失敗は無視
    }
  }, [videoSettings]);

  // 「一時的な設定適用」用。ここに入っているキーの永続化エフェクトを 1 回だけ
  // スキップする。applyTransientSettings が値をセットし、各エフェクトが消化する。
  const skipPersistOnceRef = useRef<Set<string>>(new Set());

  // generation_modelの変更をLocalStorageに保存
  useEffect(() => {
    if (skipPersistOnceRef.current.has('generation_model')) {
      skipPersistOnceRef.current.delete('generation_model');
      return;
    }
    if (settings.generation_model) {
      localStorage.setItem(STORAGE_KEY_GENERATION_MODEL, settings.generation_model);
    }
  }, [settings.generation_model]);

  // aspect_ratioの変更をLocalStorageに保存
  useEffect(() => {
    if (skipPersistOnceRef.current.has('aspect_ratio')) {
      skipPersistOnceRef.current.delete('aspect_ratio');
      return;
    }
    if (settings.aspect_ratio) {
      localStorage.setItem(STORAGE_KEY_ASPECT_RATIO, settings.aspect_ratio);
    }
  }, [settings.aspect_ratio]);

  // exclude_text_responseの変更をLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_EXCLUDE_TEXT, String(settings.exclude_text_response ?? false));
  }, [settings.exclude_text_response]);

  // 「AIに送る情報」トグル (ロゴ / 商品情報 / ブランド情報) の変更を localStorage に保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_LOGO, String(settings.include_logo ?? false));
  }, [settings.include_logo]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_PRODUCT_INFO, String(settings.include_product_info ?? false));
  }, [settings.include_product_info]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_BRAND_INFO, String(settings.include_brand_info ?? false));
  }, [settings.include_brand_info]);

  // 媒体要件の最後の選択を保存。
  // - platform: 単一値 (最後に選んだプラットフォーム)
  // - menu: platform → menu のマップ (各プラットフォームの最後の menu を覚える)
  // - spec: menu → spec のマップ (各メニューの最後の spec を覚える)
  // null クリア時は値を消さず保持する (例: 親切替時に一時的に null になっても次選択した親の記憶は残す)。
  useEffect(() => {
    if (skipPersistOnceRef.current.has('media_platform_id')) {
      skipPersistOnceRef.current.delete('media_platform_id');
      return;
    }
    if (settings.media_platform_id) {
      localStorage.setItem(STORAGE_KEY_MEDIA_PLATFORM, settings.media_platform_id);
    } else {
      localStorage.removeItem(STORAGE_KEY_MEDIA_PLATFORM);
    }
  }, [settings.media_platform_id]);
  useEffect(() => {
    const platformId = settings.media_platform_id;
    const menuId = settings.media_menu_id;
    if (!platformId || !menuId) return;
    const map = loadStringMap(STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM);
    if (map[platformId] === menuId) return;
    map[platformId] = menuId;
    saveStringMap(STORAGE_KEY_MEDIA_LAST_MENU_BY_PLATFORM, map);
  }, [settings.media_platform_id, settings.media_menu_id]);
  useEffect(() => {
    const menuId = settings.media_menu_id;
    const specId = settings.media_spec_id;
    if (!menuId || !specId) return;
    const map = loadStringMap(STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU);
    if (map[menuId] === specId) return;
    map[menuId] = specId;
    saveStringMap(STORAGE_KEY_MEDIA_LAST_SPEC_BY_MENU, map);
  }, [settings.media_menu_id, settings.media_spec_id]);

  const restoreSettings = useCallback((next: Partial<GenerationSettings> & { attributes: Record<string, unknown> }) => {
    setSettingsState((prev) => ({
      ...prev,
      ...next,
      attributes: next.attributes, // 生成履歴からの復元: shallow-merge ではなく丸ごと置換
    }));
  }, []);

  const setSettings = useCallback((next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => {
    setSettingsState((prev) => {
      const nextValue = typeof next === 'function' ? next(prev) : next;
      const merged = {
        ...prev,
        ...nextValue,
        attributes: {
          ...prev.attributes,
          ...(nextValue.attributes || {}),
        },
      };
      // aspect_ratioが変更された場合、attributes.aspect_ratioも同期する
      // （setAttribute内でattributes.aspect_ratioがsettings.aspect_ratioに反映されるため、
      //   ここで同期しないと背景変更時などに古い値で上書きされてしまう）
      if ('aspect_ratio' in nextValue) {
        merged.attributes = { ...merged.attributes, aspect_ratio: merged.aspect_ratio };
      }
      return merged;
    });
  }, []);

  const applyTransientSettings = useCallback(
    (partial: Partial<GenerationSettings>, ephemeralKeys: string[]) => {
      skipPersistOnceRef.current = new Set(ephemeralKeys);
      setSettings((prev) => ({ ...prev, ...partial }));
      // 値が変わらずエフェクトが発火しなかったキーの取りこぼしを防ぐため、次のマクロ
      // タスクで残りをクリアする（永続化エフェクトはそれより前に flush 済み）。
      setTimeout(() => {
        skipPersistOnceRef.current.clear();
      }, 0);
    },
    [setSettings],
  );

  const setAttribute = useCallback((key: string, value: unknown) => {
    setSettingsState((prev) => {
      const nextAttributes = {
        ...prev.attributes,
        [key]: value,
      };

      const nextSettings: GenerationSettings = {
        ...prev,
        attributes: nextAttributes,
      };

      // 人物属性 (gender/ethnicity/age) の settings 直下ミラーは廃止。
      // attributes.person 配下が唯一の正本 (settingsStorage.ts のコメント参照)。
      if (typeof nextAttributes.background === 'string') {
        nextSettings.background = nextAttributes.background;
      }
      if (typeof nextAttributes.hand === 'string') {
        nextSettings.hand = nextAttributes.hand as GenerationSettings['hand'];
      }
      if (typeof nextAttributes.orientation === 'string') {
        nextSettings.orientation = nextAttributes.orientation;
      }
      if (typeof nextAttributes.effect === 'string') {
        nextSettings.effect = nextAttributes.effect;
      }
      if (typeof nextAttributes.aspect_ratio === 'string') {
        nextSettings.aspect_ratio = nextAttributes.aspect_ratio;
      }

      return nextSettings;
    });
  }, []);

  const setAttributeTree = useCallback((nodes: AttributeTreeNode[]) => {
    setAttributeTreeState(nodes);
  }, []);

  const setPendingEdit = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setPendingMode('prompt');
  }, []);

  const setPendingVideoGeneration = useCallback((prompt?: string) => {
    if (prompt) {
      setPendingPrompt(prompt);
    }
    setPendingMode('video');
    // プロンプトモード＋最初のフレームから動画生成モードを設定
    setVideoGenerationMode('frame');
  }, [setVideoGenerationMode]);

  const clearPendingEdit = useCallback(() => {
    setPendingPrompt(null);
    setPendingMode(null);
  }, []);

  const value = useMemo<SettingsContextValue>(() => ({
    settings,
    setSettings,
    restoreSettings,
    applyTransientSettings,
    setAttribute,
    attributeTree,
    setAttributeTree,
    maxReferenceCount,
    setMaxReferenceCount,
    maxReferenceVideos,
    setMaxReferenceVideos,
    maxReferenceAudios,
    setMaxReferenceAudios,
    castingPosition,
    setCastingPosition,
    pendingPrompt,
    pendingMode,
    setPendingEdit,
    setPendingVideoGeneration,
    clearPendingEdit,
    templateReferenceUrl,
    setTemplateReferenceUrl,
    productionMode,
    setProductionMode,
    videoGenerationMode,
    setVideoGenerationMode,
    setVideoGenerationModeTransient,
    restoreVideoGenerationMode,
    promptVideoGenerationMode,
    setPromptVideoGenerationMode,
    scenarioInputTab,
    setScenarioInputTab,
    selectedCasting,
    setSelectedCasting,
    additionalCastings,
    setAdditionalCastings,
    castingSelectionState,
    setCastingSelectionState,
    markCastingExplicit,
    pendingDeepLinkCastScenarioId,
    setPendingDeepLinkCastScenarioId,
    consumePendingDeepLinkCast,
    selectedSeedancePresetActor: selectedSeedancePresetActorState,
    setSelectedSeedancePresetActor,
    additionalSeedancePresetActors,
    setAdditionalSeedancePresetActors,
    videoSettings,
    setVideoSettings,
    avoidFaces,
    setAvoidFaces,
    illustrationStoryboard,
    setIllustrationStoryboard,
    visualStyle,
    setVisualStyle,
    shootingMethod,
    setShootingMethod,
    dialect,
    setDialect,
    motionGraphics,
    setMotionGraphics,
    targetGender,
    setTargetGender,
    targetAge,
    setTargetAge,
    visualStyleIsStylized,
    includeStoryboardLogo,
    setIncludeStoryboardLogo,
    includeStoryboardProductImage,
    setIncludeStoryboardProductImage,
  }), [
    settings, setSettings, restoreSettings, applyTransientSettings, setAttribute,
    attributeTree, setAttributeTree, maxReferenceCount, maxReferenceVideos, maxReferenceAudios, castingPosition,
    pendingPrompt, pendingMode, setPendingEdit, setPendingVideoGeneration, clearPendingEdit,
    templateReferenceUrl, productionMode, videoGenerationMode, setVideoGenerationMode,
    setVideoGenerationModeTransient, restoreVideoGenerationMode,
    promptVideoGenerationMode, setPromptVideoGenerationMode, scenarioInputTab,
    selectedCasting, additionalCastings, castingSelectionState, markCastingExplicit,
    pendingDeepLinkCastScenarioId, setPendingDeepLinkCastScenarioId, consumePendingDeepLinkCast,
    selectedSeedancePresetActorState,
    setSelectedSeedancePresetActor, additionalSeedancePresetActors,
    videoSettings, avoidFaces, illustrationStoryboard, visualStyle, visualStyleIsStylized, shootingMethod, dialect, motionGraphics,
    targetGender, targetAge,
    includeStoryboardLogo, includeStoryboardProductImage,
  ]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

/** 生成設定だけを購読する。属性ツリー / ジョブキューの更新では再レンダーしない。 */
export function useGenerationSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error('useGenerationSettings must be used within GenerationProvider');
  }
  return ctx;
}

/** Provider 外では null を返す版 (合成ファサード用)。 */
export function useGenerationSettingsSafe(): SettingsContextValue | null {
  return useContext(SettingsContext);
}
